/**
 * Critical system problem pushes (notification type `systemProblem`), sent
 * once to every admin and support user when a critical problem opens.
 *
 * The payload's `data` is `{ systemProblemId, type, severity }` - and `type`
 * there is the PROBLEM's type (`vDriveAutoAssignFailed`, ...), not
 * `systemProblem`, so the id is what identifies one. Mirrors
 * `isSystemProblemPush` in `pwa/swCore.ts`; the two cannot share code (one
 * runs in a service worker).
 */
export const isSystemProblemPush = (data?: Record<string, string>) =>
  !!data?.systemProblemId;

/**
 * Draw a critical problem's push ourselves while the app is in front.
 *
 * FCM only shows one when no window of the app is visible, and the
 * `/system-problems` socket only runs on the Dashboard - so support working on
 * any other admin page would otherwise get nothing at all. Drawn through the
 * service worker so a tap goes through the same notificationclick as a
 * background push and opens the problem.
 */
export const showSystemProblemNotification = async ({
  title,
  body,
  data,
}: {
  title: string;
  body: string;
  data: Record<string, string>;
}) => {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return;

  await registration.showNotification(title, {
    body,
    icon: '/pwa-192x192.png',
    /* Critical by definition: it waits to be dismissed rather than sliding
       away after a few seconds. */
    requireInteraction: true,
    /* One per problem, so the same push arriving twice does not stack. */
    tag: `system-problem-${data.systemProblemId}`,
    data,
  });
};
